/**
 * Canvas2D - Main 2D rendering component for the stage designer
 * Handles rendering, input, and coordinate management
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useEditorStore } from '@/core/stores/editor-store';
import { useStageStore } from '@/core/stores/stage-store';
import { useHistoryStore } from '@/core/stores/history-store';
import {
  Vec2,
  worldToScreen,
  screenToWorld,
  snapToGrid,
  getCanvasMousePos,
  clamp,
  MAX_ZOOM_PX_PER_M,
} from './coordinates';
import {
  renderStageBounds,
  renderGrid,
  renderWall,
  renderPosition,
  renderObject,
  renderAnnotation,
  renderSelectionBox,
  renderPlacementPreview,
  renderAlignmentGuides,
  clearCanvas,
} from './renderer';
import {
  hitTestAll,
  hitTestObjects,
  getObjectsInRect,
  getPositionsInRect,
} from './hit-test';
import { getToolHandler } from '@/tools/tool-handler';
import type { StageLayout, StageObjectType, ObjectVariant } from '@/stage/types';

/**
 * Canvas2D Component
 * Top-level rendering and input handling
 */
export const Canvas2D: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Zustand stores
  const editorStore = useEditorStore();
  const stageStore = useStageStore();
  const historyStore = useHistoryStore();

  // Local state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<Vec2 | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });

  // Refs for input handling
  const dragState = useRef({
    startPos: { x: 0, y: 0 },
    startPan: { x: 0, y: 0 },
    startWorld: { x: 0, y: 0 },
    isDraggingObject: false,
    draggedType: null as string | null,
    // Frozen per-object positions at drag start so alignment snapping can
    // compute an absolute target position each frame without fighting itself.
    originals: new Map<string, { x: number; y: number; isPos: boolean }>(),
  });

  // Alignment guides that should be drawn this frame while dragging in the
  // select / move tools. Populated during mouseMove, consumed by the render
  // loop, cleared on mouseUp.
  const activeGuides = useRef<{ x: number[]; y: number[] }>({ x: [], y: [] });

  // Scale-tool state: keeps the per-object original scale + the radial
  // distance from the object center to the cursor at drag start. mouseMove
  // divides the current distance by startDist to get the scale multiplier.
  const scaleState = useRef<{
    active: boolean;
    startDist: number;
    startWorldDelta: Vec2;
    originals: Map<string, { scaleX: number; scaleY: number; cx: number; cy: number }>;
  }>({ active: false, startDist: 0, startWorldDelta: { x: 0, y: 0 }, originals: new Map() });

  // Track actual cursor position for rendering previews (avoids fake MouseEvent)
  const cursorScreenPos = useRef<Vec2>({ x: 0, y: 0 });

  // Track whether we have already auto-fitted the view (run only once)
  const hasInitializedView = useRef(false);

  const layout = stageStore.layout as StageLayout | null;

  // Update canvas size on resize
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setCanvasSize({ width: rect.width, height: rect.height });
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Initial size
    handleResize();

    return () => resizeObserver.disconnect();
  }, []);

  // Sync canvas element size when state size changes
  useEffect(() => {
    if (canvasRef.current) {
      canvasRef.current.width = canvasSize.width;
      canvasRef.current.height = canvasSize.height;
    }
  }, [canvasSize]);

  // Auto-fit: compute zoom & panOffset so the stage fills the viewport on first load.
  // Runs once when both layout and a valid canvas size are available.
  useEffect(() => {
    if (!layout || hasInitializedView.current) return;
    if (canvasSize.width <= 1 || canvasSize.height <= 1) return;

    const stageW = layout.canvas.width_m;
    const stageH = layout.canvas.height_m;
    const pad = 40; // px padding around the stage

    const fitZoom = Math.min(
      (canvasSize.width - pad * 2) / stageW,
      (canvasSize.height - pad * 2) / stageH
    );
    const panX = (canvasSize.width - stageW * fitZoom) / 2;
    const panY = (canvasSize.height - stageH * fitZoom) / 2;

    useEditorStore.getState().setZoom(fitZoom);
    useEditorStore.getState().setPanOffset({ x: panX, y: panY });
    hasInitializedView.current = true;
  }, [layout, canvasSize]);

  // Continuous render loop using requestAnimationFrame.
  // Reads latest state from Zustand stores on each frame to ensure smooth
  // rendering of cursor-dependent overlays (placement preview, selection box).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let frameId: number;

    const animate = () => {
      // Read latest store state each frame (avoids stale closure issues)
      const editor = useEditorStore.getState();
      const stage = useStageStore.getState();
      const currentLayout = stage.layout as StageLayout | null;

      if (!currentLayout) {
        frameId = requestAnimationFrame(animate);
        return;
      }

      // Clear canvas (off-stage background)
      clearCanvas(ctx, canvas.width, canvas.height, '#f3f4f6');

      // Draw stage playable-area boundary (width_m × height_m) under the grid
      renderStageBounds(ctx, currentLayout.canvas, editor.zoom, editor.panOffset);

      // Render grid
      if (editor.showGrid) {
        renderGrid(
          ctx,
          currentLayout.canvas,
          editor.zoom,
          editor.panOffset,
          canvas.width,
          canvas.height,
          editor.gridSize
        );
      }

      // Render walls
      for (const wall of currentLayout.walls) {
        const isSelected = editor.selectedObjectIds.includes(wall.id);
        renderWall(ctx, wall, editor.zoom, editor.panOffset, isSelected);
      }

      // Render shooting positions
      for (const pos of currentLayout.shooting_positions) {
        const isSelected = editor.selectedObjectIds.includes(pos.id);
        renderPosition(ctx, pos, editor.zoom, editor.panOffset, isSelected);
      }

      // Render stage objects
      for (const obj of currentLayout.objects) {
        const isSelected = editor.selectedObjectIds.includes(obj.id);
        const isHovered = editor.hoveredObjectId === obj.id;
        renderObject(ctx, obj, editor.zoom, editor.panOffset, isSelected, isHovered);
      }

      // Render annotations
      for (const ann of currentLayout.annotations) {
        renderAnnotation(ctx, ann, editor.zoom, editor.panOffset);
      }

      // Render alignment guides while dragging
      if (activeGuides.current.x.length > 0 || activeGuides.current.y.length > 0) {
        renderAlignmentGuides(
          ctx,
          activeGuides.current.x,
          activeGuides.current.y,
          editor.zoom,
          editor.panOffset,
          canvas.width,
          canvas.height,
        );
      }

      // Render selection box using tracked cursor position
      if (isSelecting && dragStart) {
        renderSelectionBox(ctx, dragStart, cursorScreenPos.current);
      }

      // Render placement preview at actual cursor position
      if (editor.activeTool === 'place' && editor.placingObjectType) {
        const worldPos = screenToWorld(cursorScreenPos.current, editor.zoom, editor.panOffset);
        renderPlacementPreview(
          ctx,
          editor.placingObjectType,
          editor.placingObjectVariant,
          worldPos,
          editor.zoom,
          editor.panOffset
        );
      }

      // Render wall-draw polyline preview (world-space) when that tool is active
      if (editor.activeTool === 'wall_draw') {
        const handler = getToolHandler('wall_draw');
        if (handler.renderPreview) {
          ctx.save();
          ctx.translate(editor.panOffset.x, editor.panOffset.y);
          ctx.scale(editor.zoom, editor.zoom);
          handler.renderPreview(ctx, editor.zoom, editor.panOffset);
          ctx.restore();
        }
      }

      frameId = requestAnimationFrame(animate);
    };

    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [canvasSize, isSelecting, dragStart]);

  // Mouse event handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!layout) return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      const mousePos = getCanvasMousePos(e.nativeEvent, canvas);
      const worldPos = screenToWorld(mousePos, editorStore.zoom, editorStore.panOffset);

      dragState.current.startPos = mousePos;
      dragState.current.startPan = { ...editorStore.panOffset };
      dragState.current.startWorld = { ...worldPos };

      // Handle different tools
      if (editorStore.activeTool === 'select') {
        // Hit test
        const hit = hitTestAll(worldPos, layout);

        if (hit) {
          const multi = e.ctrlKey || e.metaKey;

          // Clicking a grouped object selects every sibling in the same group
          // (unless Alt is held, which picks just the clicked object).
          if (hit.type === 'object' && !e.altKey) {
            const clicked = layout.objects.find((o) => o.id === hit.id);
            if (clicked?.group_id) {
              const siblings = layout.objects
                .filter((o) => o.group_id === clicked.group_id)
                .map((o) => o.id);
              if (multi) {
                for (const id of siblings) editorStore.selectObject(id, true);
              } else {
                editorStore.deselectAll();
                for (const id of siblings) editorStore.selectObject(id, true);
              }
            } else {
              editorStore.selectObject(hit.id, multi);
            }
          } else {
            editorStore.selectObject(hit.id, multi);
          }

          // Push pre-drag state to history so drag can be undone
          if (stageStore.layout) {
            historyStore.pushState(stageStore.layout);
          }
          dragState.current.isDraggingObject = true;
          dragState.current.draggedType = hit.type;
          dragState.current.originals = new Map();
          // Wait a microtask so editorStore.selectObject's state has landed,
          // but we already have the intended selection in hand either from
          // group expansion or from the multi/single call above. Rebuild from
          // the freshly-updated store:
          const selected = useEditorStore.getState().selectedObjectIds;
          for (const id of selected) {
            const o = layout.objects.find((x) => x.id === id);
            if (o) {
              dragState.current.originals.set(id, {
                x: o.position.x, y: o.position.y, isPos: false,
              });
              continue;
            }
            const p = layout.shooting_positions.find((x) => x.id === id);
            if (p) {
              dragState.current.originals.set(id, {
                x: p.position.x, y: p.position.y, isPos: true,
              });
            }
          }
          setIsDragging(true);
        } else {
          // Start selection box drag
          editorStore.deselectAll();
          setDragStart(mousePos);
          setIsSelecting(true);
        }
      } else if (editorStore.activeTool === 'move') {
        // Move tool — click on object to start dragging it
        const hit = hitTestObjects(worldPos, layout.objects);
        if (hit) {
          editorStore.selectObject(hit, false);
          if (stageStore.layout) {
            historyStore.pushState(stageStore.layout);
          }
          dragState.current.isDraggingObject = true;
          dragState.current.originals = new Map();
          const obj = layout.objects.find((o) => o.id === hit);
          if (obj) {
            dragState.current.originals.set(hit, {
              x: obj.position.x, y: obj.position.y, isPos: false,
            });
          }
          setIsDragging(true);
        }
      } else if (editorStore.activeTool === 'place') {
        // Place object at clicked position
        if (editorStore.placingObjectType) {
          // Push pre-mutation state to history so placement can be undone
          if (stageStore.layout) {
            historyStore.pushState(stageStore.layout);
          }
          const snapPos = editorStore.snapToGrid
            ? snapToGrid(worldPos, editorStore.gridSize)
            : worldPos;

          stageStore.addObject({
            id: uuidv4(),
            type: editorStore.placingObjectType as StageObjectType,
            variant: (editorStore.placingObjectVariant as ObjectVariant) || undefined,
            position: { x: snapPos.x, y: snapPos.y, z: 0 },
            rotation: { x: 0, y: 0, z: 0 },
            scale: { x: 1, y: 1, z: 1 },
            on_stand: false,
            stand_height_m: 0,
            locked: false,
            facing_direction: 0,
          });

          editorStore.setDirty(true);
        }
      } else if (editorStore.activeTool === 'scale') {
        // Scale tool — click on an object (or use current selection) and drag
        // outward to grow, inward to shrink. Hold Shift for non-uniform per-axis.
        const hit = hitTestObjects(worldPos, layout.objects);
        const targetIds = hit ? [hit] : editorStore.selectedObjectIds;
        if (targetIds.length === 0) return;

        if (hit) editorStore.selectObject(hit, false);
        if (stageStore.layout) historyStore.pushState(stageStore.layout);

        const originals = new Map<string, { scaleX: number; scaleY: number; cx: number; cy: number }>();
        for (const id of targetIds) {
          const o = layout.objects.find((obj) => obj.id === id);
          if (!o) continue;
          originals.set(id, {
            scaleX: o.scale.x || 1,
            scaleY: o.scale.y || 1,
            cx: o.position.x,
            cy: o.position.y,
          });
        }
        // Use the first object's center as the reference point for uniform scaling
        const first = originals.values().next().value;
        const dx = worldPos.x - (first?.cx ?? worldPos.x);
        const dy = worldPos.y - (first?.cy ?? worldPos.y);
        const startDist = Math.max(0.05, Math.sqrt(dx * dx + dy * dy));

        scaleState.current = {
          active: true,
          startDist,
          startWorldDelta: { x: dx, y: dy },
          originals,
        };
        setIsDragging(true);
      } else if (editorStore.activeTool === 'measure') {
        // Start measurement
        setDragStart(worldToScreen(worldPos, editorStore.zoom, editorStore.panOffset));
        setIsDragging(true);
      } else if (editorStore.activeTool === 'wall_draw') {
        // Delegate to the wall tool handler (stateful polyline + double-click finish)
        const handler = getToolHandler('wall_draw');
        handler.onMouseDown?.({
          worldPos,
          screenPos: mousePos,
          shiftKey: e.shiftKey,
          ctrlKey: e.ctrlKey,
          button: e.button,
        });
      }
    },
    [layout, editorStore, stageStore, historyStore]
  );

  // Drag-and-drop from the Object Library panel. The library sets the
  // dataTransfer payload to `{ type, variant }`; we convert the drop point
  // to world space and create an object at that position.
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const data = e.dataTransfer.getData('application/x-stage-object');
      if (!data || !canvasRef.current) return;
      let payload: { type: string; variant?: string };
      try {
        payload = JSON.parse(data);
      } catch {
        return;
      }
      if (!layout || !payload.type) return;

      const mousePos = getCanvasMousePos(e.nativeEvent as unknown as MouseEvent, canvasRef.current);
      const worldPos = screenToWorld(mousePos, editorStore.zoom, editorStore.panOffset);
      const snapPos = editorStore.snapToGrid
        ? snapToGrid(worldPos, editorStore.gridSize)
        : worldPos;

      if (stageStore.layout) historyStore.pushState(stageStore.layout);
      stageStore.addObject({
        id: uuidv4(),
        type: payload.type as StageObjectType,
        variant: (payload.variant as ObjectVariant) || undefined,
        position: { x: snapPos.x, y: snapPos.y, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        scale: { x: 1, y: 1, z: 1 },
        on_stand: false,
        stand_height_m: 0,
        locked: false,
        facing_direction: 0,
      });
      editorStore.setDirty(true);
    },
    [layout, editorStore, stageStore, historyStore],
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (e.dataTransfer.types.includes('application/x-stage-object')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  }, []);

  // Double-click on the canvas finishes a wall-in-progress
  const handleDoubleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (editorStore.activeTool !== 'wall_draw') return;
      const handler = getToolHandler('wall_draw');
      // wall-tool treats two quick clicks as a finish via its own lastClickTime
      // logic, so the double-click handler just ensures a trailing onKeyDown
      // fires the same finisher in case the threshold was exceeded.
      handler.onKeyDown?.('Enter', {});
      e.preventDefault();
    },
    [editorStore.activeTool]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!layout || !canvasRef.current) return;

      const canvas = canvasRef.current;
      const mousePos = getCanvasMousePos(e.nativeEvent, canvas);
      const worldPos = screenToWorld(mousePos, editorStore.zoom, editorStore.panOffset);

      // Track cursor position for the render loop (placement preview, selection box)
      cursorScreenPos.current = mousePos;

      // Feed cursor position to tool handlers that maintain in-progress state
      if (editorStore.activeTool === 'wall_draw') {
        getToolHandler('wall_draw').onMouseMove?.({
          worldPos,
          screenPos: mousePos,
          shiftKey: e.shiftKey,
          ctrlKey: e.ctrlKey,
          button: 0,
        });
      }

      // Update hover state
      const hit = hitTestAll(worldPos, layout);
      if (hit) {
        editorStore.setHovered(hit.id);
      } else {
        editorStore.setHovered(null);
      }

      // Handle dragging
      if (isDragging) {
        if (editorStore.activeTool === 'scale' && scaleState.current.active) {
          // Scale — compute multiplier from radial distance vs start distance.
          // Shift: per-axis based on which axis the cursor moved most along.
          const s = scaleState.current;
          const cx = s.originals.values().next().value?.cx ?? worldPos.x;
          const cy = s.originals.values().next().value?.cy ?? worldPos.y;
          const dx = worldPos.x - cx;
          const dy = worldPos.y - cy;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const uniform = Math.max(0.05, dist / s.startDist);

          let sx = uniform;
          let sy = uniform;
          if (e.shiftKey) {
            sx = Math.max(0.05, Math.abs(dx) / Math.max(0.05, Math.abs(s.startWorldDelta.x)));
            sy = Math.max(0.05, Math.abs(dy) / Math.max(0.05, Math.abs(s.startWorldDelta.y)));
          }

          for (const [id, orig] of s.originals) {
            stageStore.updateObject(id, {
              scale: { x: orig.scaleX * sx, y: orig.scaleY * sy, z: 1 },
            });
          }
        } else if (editorStore.activeTool === 'select' && isSelecting) {
          // Selection box is being drawn
          // (rendering handled in render loop)
        } else if (
          (editorStore.activeTool === 'select' || editorStore.activeTool === 'move') &&
          dragState.current.isDraggingObject
        ) {
          // Absolute-delta drag: compute each object's target position from
          // the cursor's world delta since drag start. This lets us snap to
          // alignment / grid without fighting the drag on subsequent frames.
          const deltaX = worldPos.x - dragState.current.startWorld.x;
          const deltaY = worldPos.y - dragState.current.startWorld.y;

          // Pick a "lead" selection (first in the selection list) and find its
          // desired centre so we can compute one alignment offset that applies
          // to every selected object uniformly.
          const selectedIds = editorStore.selectedObjectIds;
          const leadId = selectedIds[0];
          const leadOrig = dragState.current.originals.get(leadId);
          if (!leadOrig) return;

          const leadTargetX = leadOrig.x + deltaX;
          const leadTargetY = leadOrig.y + deltaY;

          // Snap threshold in world space (0.15 m) — big enough to feel sticky,
          // small enough not to trap the cursor. Held-Alt disables alignment
          // snap so users can place objects close without the guide grabbing.
          const ALIGN_DIST = 0.15;
          let snapX = leadTargetX;
          let snapY = leadTargetY;
          const guideXs: number[] = [];
          const guideYs: number[] = [];

          if (!e.altKey) {
            let bestX = ALIGN_DIST;
            let bestY = ALIGN_DIST;
            for (const o of layout.objects) {
              if (selectedIds.includes(o.id)) continue;
              const dx = Math.abs(o.position.x - leadTargetX);
              const dy = Math.abs(o.position.y - leadTargetY);
              if (dx < bestX) {
                bestX = dx;
                snapX = o.position.x;
              }
              if (dy < bestY) {
                bestY = dy;
                snapY = o.position.y;
              }
            }
            for (const p of layout.shooting_positions) {
              if (selectedIds.includes(p.id)) continue;
              const dx = Math.abs(p.position.x - leadTargetX);
              const dy = Math.abs(p.position.y - leadTargetY);
              if (dx < bestX) { bestX = dx; snapX = p.position.x; }
              if (dy < bestY) { bestY = dy; snapY = p.position.y; }
            }
            if (snapX !== leadTargetX) guideXs.push(snapX);
            if (snapY !== leadTargetY) guideYs.push(snapY);
          }

          // Grid snap still applies if the user enabled it and alignment
          // didn't already grab the axis.
          if (editorStore.snapToGrid) {
            const g = editorStore.gridSize;
            if (guideXs.length === 0) snapX = Math.round(snapX / g) * g;
            if (guideYs.length === 0) snapY = Math.round(snapY / g) * g;
          }

          const snapDeltaX = snapX - leadOrig.x;
          const snapDeltaY = snapY - leadOrig.y;
          activeGuides.current = { x: guideXs, y: guideYs };

          for (const id of selectedIds) {
            const orig = dragState.current.originals.get(id);
            if (!orig) continue;
            const nx = orig.x + snapDeltaX;
            const ny = orig.y + snapDeltaY;
            if (orig.isPos) {
              const pos = layout.shooting_positions.find((p) => p.id === id);
              if (pos) {
                stageStore.updatePosition(id, {
                  position: { x: nx, y: ny, z: pos.position.z },
                });
              }
            } else {
              const obj = layout.objects.find((o) => o.id === id);
              if (obj) {
                stageStore.updateObject(id, {
                  position: { x: nx, y: ny, z: obj.position.z },
                });
              }
            }
          }
        }
      }
    },
    [layout, editorStore, stageStore, isDragging, isSelecting]
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!layout) return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      const mousePos = getCanvasMousePos(e.nativeEvent, canvas);

      // Finish selection box
      if (isSelecting && dragStart) {
        const worldStart = screenToWorld(dragStart, editorStore.zoom, editorStore.panOffset);
        const worldEnd = screenToWorld(mousePos, editorStore.zoom, editorStore.panOffset);

        const topLeft = {
          x: Math.min(worldStart.x, worldEnd.x),
          y: Math.min(worldStart.y, worldEnd.y),
        };
        const bottomRight = {
          x: Math.max(worldStart.x, worldEnd.x),
          y: Math.max(worldStart.y, worldEnd.y),
        };

        const selectedObjs = getObjectsInRect(topLeft, bottomRight, layout.objects);
        const selectedPos = getPositionsInRect(topLeft, bottomRight, layout.shooting_positions);
        const allSelected = [...selectedObjs, ...selectedPos];
        for (let i = 0; i < allSelected.length; i++) {
          editorStore.selectObject(allSelected[i], i > 0);
        }

        setIsSelecting(false);
        setDragStart(null);
      }

      setIsDragging(false);
      dragState.current.isDraggingObject = false;
      dragState.current.draggedType = null;
      dragState.current.originals.clear();
      activeGuides.current = { x: [], y: [] };
      scaleState.current.active = false;
      editorStore.setDirty(true);
    },
    [layout, editorStore, isSelecting, dragStart]
  );

  // Zoom with mouse wheel
  const handleWheel = useCallback(
    (e: React.WheelEvent<HTMLCanvasElement>) => {
      e.preventDefault();

      const canvas = canvasRef.current;
      if (!canvas) return;

      const mousePos = getCanvasMousePos(e.nativeEvent, canvas);
      const worldPos = screenToWorld(mousePos, editorStore.zoom, editorStore.panOffset);

      // Compute minimum zoom so viewport never shows more than 3× the stage size
      const currentLayout = useStageStore.getState().layout as StageLayout | null;
      let minZoom = 0.1;
      if (currentLayout) {
        minZoom = Math.max(
          canvas.width / (3 * currentLayout.canvas.width_m),
          canvas.height / (3 * currentLayout.canvas.height_m)
        );
      }

      // Zoom in/out. Zoom is px-per-meter (auto-fit sets ~30-70), so the max
      // must be far above 1 — the old 5.0 cap (from 1.0-based zoom semantics)
      // made min > max and wedged the zoom on the first wheel event.
      const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
      const newZoom = clamp(editorStore.zoom * zoomFactor, minZoom, MAX_ZOOM_PX_PER_M);

      // Keep world position under mouse constant
      const newPan = {
        x: mousePos.x - worldPos.x * newZoom,
        y: mousePos.y - worldPos.y * newZoom,
      };

      editorStore.setZoom(newZoom);
      editorStore.setPanOffset(newPan);
    },
    [editorStore]
  );

  // Canvas-specific keyboard handlers (Delete, Undo, Redo, Save, tool shortcuts
  // are handled globally in DesignerPage — do NOT duplicate them here)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!layout) return;

      // Duplicate: Ctrl+D (canvas-specific: needs access to object positions)
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault();
        if (stageStore.layout) {
          historyStore.pushState(stageStore.layout);
        }
        for (const id of editorStore.selectedObjectIds) {
          const obj = layout.objects.find((o) => o.id === id);
          if (obj) {
            stageStore.addObject({
              ...obj,
              id: uuidv4(),
              position: {
                x: obj.position.x + 0.5,
                y: obj.position.y + 0.5,
                z: obj.position.z,
              },
            });
          }
        }
        editorStore.setDirty(true);
      }

      // Escape: deselect or cancel placing / wall-draw
      if (e.key === 'Escape') {
        e.preventDefault();
        editorStore.deselectAll();
        if (editorStore.activeTool === 'place') {
          editorStore.setPlacing(null);
          editorStore.setTool('select');
        }
        if (editorStore.activeTool === 'wall_draw') {
          getToolHandler('wall_draw').onKeyDown?.('Escape', {});
        }
      }

      // Enter finishes the wall-in-progress
      if (e.key === 'Enter' && editorStore.activeTool === 'wall_draw') {
        e.preventDefault();
        getToolHandler('wall_draw').onKeyDown?.('Enter', {});
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [layout, editorStore, stageStore, historyStore]);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full bg-white border border-gray-200 overflow-hidden"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <canvas
        ref={canvasRef}
        className={`block w-full h-full ${
          editorStore.activeTool === 'wall_draw' ||
          editorStore.activeTool === 'measure' ||
          editorStore.activeTool === 'place'
            ? 'cursor-crosshair'
            : editorStore.activeTool === 'scale'
            ? 'cursor-nwse-resize'
            : 'cursor-default'
        }`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onDoubleClick={handleDoubleClick}
        onMouseLeave={() => {
          setIsDragging(false);
          dragState.current.isDraggingObject = false;
          dragState.current.draggedType = null;
        }}
        onWheel={handleWheel}
        style={{
          touchAction: 'none',
        }}
      />

      {/* Overlay when no layout loaded */}
      {!layout && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-90">
          <p>No stage layout loaded</p>
        </div>
      )}

      {/* Zoom and pan info */}
      <div className="absolute bottom-4 right-4 text-xs text-gray-600 bg-white bg-opacity-75 px-2 py-1 rounded">
        Zoom: {editorStore.zoom.toFixed(2)}x | Pan: ({editorStore.panOffset.x.toFixed(0)}, {editorStore.panOffset.y.toFixed(0)})
      </div>
    </div>
  );
};

export default Canvas2D;
